import React from 'react';
import { CobuStack, CobuText, CobuButton } from '@cobu/ui';
import { Image, useTheme } from 'tamagui';
import { PerkData } from '@cobu/types';
import { URLS } from '../../constants/Urls';
import { Strings } from '../../constants/Strings';

interface PerkProps {
  perk: PerkData;
  onPerkPress: (perkId: string, heroColor?: string) => void;
  index: number;
}

interface ReferralPerkProps extends PerkProps {
  gradientColors: readonly [string, string, ...string[]];
}

// Get strings from central repository
const strings = Strings.get();

// Shared styles (plain object to work on web/React)
const styles = {
  gradient: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: 5,
  } as any,
  merchantImage: {
    width: '100%',
    height: '100%',
    alignSelf: 'center',
  } as any,
  referralPerkGradient: {
    height: 50,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  } as any,
  regularPerkGradient: {
    width: '100%',
    height: '100%',
    borderTopLeftRadius: 10,
    borderBottomLeftRadius: 10,
  } as any,
} as const

// Minimal web-friendly LinearGradient replacement
const LocalLinearGradient = ({ colors, style, children }: { colors: readonly [string, string, ...string[]]; style?: any; children?: React.ReactNode }) => {
  const flattened = Array.isArray(style) ? Object.assign({}, ...style) : (style || {});
  const [raw1, raw2] = colors;
  const resolve = (c: string) => (typeof c === 'string' && c.startsWith('$') ? `var(--${c.slice(1)})` : c);
  const c1 = resolve(raw1 as any);
  const c2 = resolve(raw2 as any);
  return (
    <CobuStack
      style={{
        ...flattened,
        backgroundImage: `linear-gradient(135deg, ${c1}, ${c2})`,
      }}
    >
      {children}
    </CobuStack>
  );
};

// Referral Perk Component
const ReferralPerk: React.FC<ReferralPerkProps> = ({ perk, onPerkPress, gradientColors }) => {
  const theme = useTheme();
  const rawColor = gradientColors[0];
  const heroColor =
    typeof rawColor === 'string' && rawColor.startsWith('$')
      ? ((theme as any)[rawColor.slice(1)]?.val as string) || '#16a34a'
      : (rawColor as string);

  return (
  <CobuStack
  mx={'$3'}
  my={'$2'}
  br={'$3'}
  bc="$white"
  shac='$shadowGray'
  shof={{ width: 0, height: 0 }}
  shar={'$1'}
  elevation={'$1'}
  >
    <CobuButton
      variant="ghost"
      onPress={() => onPerkPress(perk.uuid, heroColor)}
      w="100%"
      h="auto"
      br={'$3'}
      bc="white"
      p="$0"
    >
      <CobuStack w="100%">
        <LocalLinearGradient
          colors={gradientColors}
          style={[
            styles.gradient,
            styles.referralPerkGradient,
          ]}
        >
          <CobuText color="$white" fow="600" fos="$5" ta="center">
            {perk.bannerTitle}
          </CobuText>
        </LocalLinearGradient>

        <CobuStack ai="center" jc="center" p="$3" gap="$2">
          <CobuText fow="bold" fos="$5" ta="center" numberOfLines={2} adjustsFontSizeToFit color="#000000">
            {perk?.name?.toUpperCase() || ''}
          </CobuText>
          <CobuText fow="300" fos="$2" ta="center" color="#9ca3af">
            {strings.perks.perkCard.tapToSeeDetails}
          </CobuText>
        </CobuStack>
      </CobuStack>
    </CobuButton>
  </CobuStack>
)};

// Regular Perk Component
const RegularPerk: React.FC<ReferralPerkProps> = ({ perk, onPerkPress, gradientColors }) => {
  const theme = useTheme();
  const rawColor = gradientColors[0];
  const heroColor =
    typeof rawColor === 'string' && rawColor.startsWith('$')
      ? ((theme as any)[rawColor.slice(1)]?.val as string) || '#16a34a'
      : (rawColor as string);

  return (
  <CobuStack
  mx={'$3'}
  my={'$2'}
  br={'$3'}
  bc="$white"
  shac='$shadowGray'
  shof={{ width: 0, height: 0 }}
  shar={'$1'}
  elevation={'$1'}
  layout="horizontal"
  minH={'$20'}
  >
    <CobuButton
      variant="ghost"
        onPress={() => onPerkPress(perk.uuid, heroColor)}
      w="100%"
      minH={'$20'}
      h="auto"
      br={'$3'}
      bc="white"
      p="$0"
    >
      <CobuStack layout="horizontal" w="100%" h="100%" ai="stretch">
        <CobuStack w="$20" h="100%">
          <LocalLinearGradient
            colors={gradientColors}
            style={[
              styles.gradient,
              styles.regularPerkGradient,
            ]}
          >
            <Image
              width="100%"
              height="100%"
              source={{
                uri: perk?.merchant?.image || URLS.DEFAULT_MERCHANT_IMAGE,
              }}
              objectFit="contain"
            />
          </LocalLinearGradient>
        </CobuStack>

        <CobuStack f={1} p="$3" jc="center" gap="$1">
          <CobuText fow="bold" fos="$4" numberOfLines={2} adjustsFontSizeToFit mb="$1" color="#000000">
            {perk?.name?.toUpperCase() || ''}
          </CobuText>
          <CobuText color="#9ca3af" fow="300" fos="$2">
            {strings.perks.perkCard.tapToSeeDetails}
          </CobuText>
        </CobuStack>
      </CobuStack>
    </CobuButton>
  </CobuStack>
)};

// Main Perk Component
export const Perk: React.FC<PerkProps> = ({ perk, onPerkPress, index }) => {
  const referralGradient = ['#32a249', '#32a249'] as const;
  const regularGradients = [
    ['#d4d4d4', '#d4d4d4'] as const,
    ['#a3a3a3', '#a3a3a3'] as const,
  ];
  const gradientColors = perk.isReferral
    ? referralGradient
    : regularGradients[index % regularGradients.length];
  return perk.isReferral ? (
    <ReferralPerk
      perk={perk}
      onPerkPress={onPerkPress}
      gradientColors={gradientColors}
      index={index}
    />
  ) : (
    <RegularPerk
      perk={perk}
      onPerkPress={onPerkPress}
      gradientColors={gradientColors}
      index={index}
    />
  );
};
